import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChange, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { StorageMap } from '@ngx-pwa/local-storage';
import { TranslateService } from '@ngx-translate/core';
import { DataTableDirective } from 'angular-datatables';
import MobileDetect from 'mobile-detect';
import moment from 'moment';
import { Subject, Subscription } from 'rxjs';
import { flatMap, takeWhile } from 'rxjs/operators';
import { SiteConfig } from '../../../../config/site-config';
import { environment } from '../../../../environments/environment';
import { AddressBookService } from '../../../address-book/services/address-book.service';
import { AppState } from '../../../app.state';
import { SharedService } from '../../../core/services/shared.service';
import { SpinnerService } from '../../../core/services/spinner/spinner.service';
import { StorageService } from '../../../core/services/storage/storage.service';

import { CarrierPipe } from '../../../history/pipes/carrier.pipe';
import { ReportHistoryService } from '../../../history/services/report-history.service';
import { ShipmentHistoryService } from '../../../history/services/shipment-history.service';
import { IHandheldSignature } from '../../../ship/models/handheld-signature.interface';
import { Package } from '../../../ship/models/package.model';
import { ShipmentService } from '../../../ship/services/shipment.service';
import { UPSPickUpType } from '../../enum/general-enum';
import { Customer } from '../../models/customer/customer.model';
import { NotificationType } from '../../models/notification-type';
import { ShipmentStatus } from '../../models/shipments/shipment-status.enum';
import { ICarrierTrackingScan } from '../../models/shipments/tracking/carrier-tracking-scan.interface';
import { ITrackingResult } from '../../models/shipments/tracking/tracking-result.interface';
import { CountryService } from '../../services/country/country.service';
import { ConfirmDialogComponent } from '../../services/dialog/confirm-dialog/confirm-dialog.component';
import { ErrorHandlerService } from '../../services/error-handler/error-handler.service';
import { FileService } from '../../services/file/file.service';
import { NotificationService } from '../../services/notification/notification.service';
import { User } from '../../services/user/models/user.model';
import { UserService } from '../../services/user/user.service';
import { UtilityService } from '../../services/utility/utility.service';
import { ShipLabelComponent } from '../ship-label/ship-label.component';

@Component({
    selector: 'upsc-ship-details',
    templateUrl: './ship-details.component.html',
    styleUrls: ['./ship-details.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ShipDetailsComponent implements OnInit, OnChanges, OnDestroy {
    @Input() shipmentPackage: Partial<Package>;
    @Input() shipmentType = 'web';
    @Input() isTrackedShipment = false;
    @Input() isPendingShipment = false;
    @Input() shouldPrintLabel = false;
    @Input() isMobileWebView = false;
    @Input() containerDialogRef: MatDialogRef<any>;
    @Output() shipmentVoiding: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('shipLabel') public shipLabel: ShipLabelComponent;
    public isDEGBUser = false;
    public isFRITUser: boolean;
    public isHKUser = false;
    public isShowTrackingDetailsTable = false;
    public labelImage: string;
    public labelPdf: string;
    public podPdf: string;
    public handheldSignature: IHandheldSignature;
    public isHandheldSignatureLoading = false;
    public insuredValueCurrencyCode = 'USD';
    public commodityCurrencyCode = 'USD';
    public weightUOM = 'LBS';
    public estimatedCost;
    public surcharge;
    public insuranceCost;
    public totalCharges;
    public shouldShowCostEstimate = false;
    @ViewChild(DataTableDirective, { static: false })
    dtElement: DataTableDirective;
    public dtOptions: any = {};
    public dtTrigger: Subject<any> = new Subject<any>();
    public trackingScans: ICarrierTrackingScan[];
    public isTrackingDetailsLoading = false;
    public carrierCode: string;
    public shouldShowBillToAccountNumber = false;
    public isVoidingShipment = false;
    public isAllowEditShipment = false;
    public isReturnService = false;
    public user: User;
    public customer: Customer;
    public isEUUser = false;
    public readonly environment = environment;
    public sessionLanguage: string;
    private locale = 'en-US';
    private storageName = 'shipment-details';
    private printCss = '';
    private winRef: Window;
    private lang = 'en';
    private getTrackingDetailsSubscription: Subscription;
    private getShipmentLabelSubscription: Subscription;
    private getHandheldSignatureSubscription: Subscription;

    public constructor(private carrierPipe: CarrierPipe,
                       private countryService: CountryService,
                       private errorHandlerService: ErrorHandlerService,
                       private fileService: FileService,
                       private notificationService: NotificationService,
                       private reportHistoryService: ReportHistoryService,
                       private router: Router,
                       private sharedService: SharedService,
                       private readonly appState: AppState,
                       private shipmentService: ShipmentService,
                       private shipmentHistoryService: ShipmentHistoryService,
                       private spinnerService: SpinnerService,
                       private storageService: StorageService,
                       private storage: StorageMap,
                       private userService: UserService,
                       private addressBookService: AddressBookService,
                       private utilityService: UtilityService,
                       public confirmDialogRef: MatDialogRef<ConfirmDialogComponent>,
                       public dialog: MatDialog,
                       private translateService: TranslateService,
                       private adapter: DateAdapter<any>,
    ) {
        this.user = this.appState.user$();
        this.customer = this.appState.customer$();

        this.storage.get('lang').subscribe(
            (lang: string) => {
                this.lang = lang || 'en';
            },
        );

        this.isHKUser = this.userService.isHKUser(this.user);
        this.isDEGBUser = this.userService.isDEGBUser(this.user);
        this.isFRITUser = this.userService.isFRITUser(this.user);
        this.isEUUser = this.userService.isEUUser(this.user?.CountryCode);
        this.insuredValueCurrencyCode = 'USD';
        if (this.shipmentService.isDEFRITUser(this.user)) {
            this.insuredValueCurrencyCode = 'EUR';
            this.locale = 'de-DE';
        }

        if (this.shipmentService.isGBUser(this.user)) {
            this.insuredValueCurrencyCode = 'GBP';
            this.locale = 'en-GB';
        }

        this.weightUOM = (this.isEUUser || this.isHKUser) ? 'KGS' : 'LBS';

        this.fileService.readFileContent(`${ window.location.origin }/assets/css/_print-ship-details.css`)
            .pipe(takeWhile(() => !this.printCss))
            .subscribe(
                (content) => {
                    this.printCss = content;
                },
            );

        this.translateService.onLangChange.subscribe((event) => {
            this.sessionLanguage = event.lang;
            this.adapter.setLocale(this.sessionLanguage);
            this.updateDTOptionsLanguage();
        });
    }

    public get shouldShowPackageDetails(): boolean {
        if (!this.shipmentPackage) {
            return false;
        }

        return this.carrierPipe.transform(this.shipmentPackage?.CarrierCode)
                   .toLowerCase() === 'ups' && this.shipmentPackage?.PackageCode === '02';
    }

    public ngOnInit() {
        this.dtOptions = SiteConfig.tableConfig;
        this.dtOptions.language = { emptyTable: this.translateService.instant('NoRecordsFound') };
    }

    public ngOnChanges(changes: SimpleChanges) {
        this.onShipmentPackageChanged(changes['shipmentPackage']);
    }

    public ngOnDestroy() {
        this.storageService.remove(this.storageName);
        this.utilityService.clearSubscriptions([
            this.getTrackingDetailsSubscription,
            this.getShipmentLabelSubscription,
            this.getHandheldSignatureSubscription,
        ]);
    }

    public formatMoney(value) {
        return this.utilityService.formatMoney(value, this.insuredValueCurrencyCode, this.locale);
    }

    public formatMoneyWithoutDefaultCurrency(value, currencyCode?: string) {
        return this.utilityService.formatMoney(value, currencyCode, this.locale);
    }

    public simplifyDate(date: Date) {
        return moment(date).format('M/D/YYYY');
    }

    public downloadLabel() {
        if (!this.shipLabel) {
            this.warnNoLabel();
            return;
        }

        this.shipLabel.downloadLabel();
    }

    public downloadPOD() {
        if (!this.shipLabel) {
            this.warnNoLabel();
            return;
        }

        this.shipLabel.downloadPOD();
    }

    public printLabel() {
        if (!this.shipLabel) {
            this.getLabel();
            // this.warnNoLabel();
            return;
        }

        this.shipLabel.printLabel();
    }

    public handleGetShipmentLabelSuccess(res) {
        if (!res || !res.label) {
            this.warnNoLabel();
            return;
        }

        this.labelPdf = res.label;
        this.shipLabel.printLabel();
    }

    public handleGetShipmentLabelFailure(err) {
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            'Failed Getting Label',
            NotificationType.ERROR);
    }

    public toggleTrackingDetails(event) {
        event.preventDefault();

        this.isShowTrackingDetailsTable = !this.isShowTrackingDetailsTable;

        if (this.isShowTrackingDetailsTable) {
            this.getTrackingDetails();
        }
    }

    public getTotalCommodityValue(): number {
        if (!this.shipmentPackage.Commodities || !this.shipmentPackage.Commodities.length) {
            return 0;
        }

        return this.shipmentPackage.Commodities.reduce(
            (sum, item) => {
                return sum + +item.CustomsValue;
            },
            0);
    }

    public printShipmentDetails() {
        if (typeof (this.winRef) !== 'undefined' && !this.winRef.closed) {
            this.winRef.focus();
            return;
        }

        const printContents = document.getElementById('js-ship-details').outerHTML;
        const htmlContent = this.generatePrintHtml(printContents, this.printCss);

        const md = new MobileDetect(window.navigator.userAgent);
        if (md.phone() === 'iPhone') {
            window.open('', '_blank', 'top=0,left=0,height=100%,width=auto')
                  .document.write(htmlContent);

            return;
        }

        // This doesn't work on Safari on iOS unless window.open() is called directly.
        this.winRef = window.open('', '_blank', 'top=0,left=0,height=800px,width=auto');
        this.winRef.document.open();
        this.winRef.document.write(htmlContent);

        this.winRef.document.close();
    }

    public hasSchedulePickup() {
        if (!this.shipmentPackage) {
            return false;
        }

        return this.shipmentPackage.UPSPickUpType === 2 ||
            this.shipmentPackage.UPSPickUpType === 5;
    }

    /**
     * Checks whether a print page should be fixed.
     *
     * @returns {boolean}
     */
    public shouldFixPrint() {
        // Firefox and IE currently don't handle printing flexbox properly.
        return !!window.navigator.userAgent.match(/(firefox\/)|(trident\/)/ig);
    }

    public shouldShowShipToReroute(): boolean {
        if (!this.shipmentPackage) {
            return false;
        }

        return this.shipmentPackage.IsShipToRestrictedZip
            && !!this.shipmentPackage.ShipToRerouting
            && Object.keys(this.shipmentPackage.ShipToRerouting).length > 0;
    }

    public shouldShowRestrictedZipCodeMessage(): boolean {
        if (!this.shipmentPackage) {
            return false;
        }

        const fromCountry = this.shipmentPackage.ShipFrom.Country ? this.shipmentPackage.ShipFrom.Country.toUpperCase() : '';
        const toCountry = this.shipmentPackage.ShipTo ? this.shipmentPackage.ShipTo.Country.toUpperCase() : '';

        return ['US', 'HK'].includes(fromCountry)
            && toCountry === 'US'
            && this.shipmentPackage.IsShipToRestrictedZip;
    }

    public showEditConfirmationDialog(event) {
        if (event && Object.hasOwn(event,'preventDefault')) {
            event.preventDefault();
        }

        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            width: '430px',
            data: {
                title: this.translateService.instant('pleaseNote'),
                titleClass: 'note-title',
                message: this.translateService.instant('editShipmentWarning'),
                yes: this.translateService.instant('proceedToEdit'),
                no: this.translateService.instant('Cancel'),
            },
        };

        this.confirmDialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);

        this.confirmDialogRef.afterClosed().subscribe(
            (result) => {
                if (!result) {
                    return;
                }

                this.isVoidingShipment = true;
                this.shipmentVoiding.emit(true);
                this.shipmentHistoryService.deleteShipment(this.shipmentPackage.ShipmentId)
                    .subscribe(
                        (res) => {
                            this.isVoidingShipment = false;
                            this.shipmentVoiding.emit(false);
                            this.editShipment();
                        },
                        (err) => {
                            this.isVoidingShipment = false;
                            this.shipmentVoiding.emit(false);
                            this.notificationService.notify(
                                this.errorHandlerService.getHttpErrorMessage(err),
                                'Failed voiding a shipment',
                                NotificationType.ERROR);
                        },
                    );
            });
    }

    public editShipment() {
        if (this.containerDialogRef) {
            setTimeout(
                () => {
                    this.containerDialogRef.close();
                },
                0);
        }

        this.restoreUpsAccessPoint();

        this.storageService.set('shipment-edit', this.shipmentPackage);
        const carrier = this.carrierPipe.transform(this.shipmentPackage.CarrierCode).toLowerCase();
        this.router.navigate([`/ship/${ carrier }`]);
    }

    public viewClaim(claimId: number): void {
        this.router.navigate(['/claims', claimId]).then(
            () => {
                if (this.containerDialogRef) {
                    setTimeout(() => {
                        this.containerDialogRef.close();
                    }, 0);
                }
            }
        );
    }

    public fileClaim(): void {
        this.router.navigate(['/claims/file-claim'], {
            state: {
                trackingNumber: this.shipmentPackage.TrackingNumber,
            },
        }).then(
            () => {
                if (this.containerDialogRef) {
                    setTimeout(() => {
                        this.containerDialogRef.close();
                    }, 0);
                }
            },
        );
    }

    public reship() {
        this.spinnerService.show();
        if (this.containerDialogRef) {
            setTimeout(
                () => {
                    this.containerDialogRef.close();
                },
                0);
        }

        this.shipmentPackage.ShipDate = moment().toDate();
        this.shipmentPackage.PickUpDate = moment().toDate();
        this.shipmentPackage.CustomerReferenceNumber = '';
        this.shipmentPackage.ReferenceNumber = '';
        this.shipmentPackage.Weight = 0;
        this.shipmentPackage.InsuredValue = 0;
        this.shipmentPackage.Commodities = null;
        this.shipmentPackage.TotalCustomsValue = '0';
        this.shipmentPackage.IsDropoff = false;

        this.restoreUpsAccessPoint();

        this.userService.getUser()
            .pipe(flatMap((user: User) => this.addressBookService.getAddressBook(user.DefaultLocation)))
            .subscribe(
                (location) => {
                    if (location) {
                        if (<UPSPickUpType>(+location.UPSPickUpType) === UPSPickUpType.SmartPickUp) {
                            // [MV3-2719] Set pickup type to smart pickup if the pickup type of default location is a smart pickup.
                            this.shipmentPackage.UPSPickUpType = UPSPickUpType.SmartPickUp;
                        }
                    }

                    const carrier = this.carrierPipe.transform(this.shipmentPackage.CarrierCode).toLowerCase();
                    this.storageService.set('shipment-edit', this.shipmentPackage);
                    this.storageService.set('reship', { isReship: true });
                    this.router.navigate([`/ship/${ carrier }`]);
                    this.spinnerService.hide();
                },
            );
    }

    public updateDTOptionsLanguage(): void {
        this.dtOptions.language = {
            emptyTable: this.translateService.instant('NoRecordsFound'),
        };

        this.rerender();
    }

    rerender(): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            // Destroy the table first

            dtInstance.destroy();


            // Call the dtTrigger to rerender again
            this.dtTrigger.next(null);
        });
    }

    private restoreUpsAccessPoint(): void {
        if (this.carrierCode !== 'ups') {
            return;
        }

        this.shipmentPackage.UseAccessPoint = this.shipmentPackage.IsHoldAtLocation;
        this.shipmentPackage.AccessPoint = this.shipmentPackage.HoldAtLocationID
            ? {
                UPSAccessPointID: this.shipmentPackage.HoldAtLocationID,
                StreetAddress: this.shipmentPackage.HoldAtAddress1,
                Consignee: this.shipmentPackage.HoldAtAddress2,
                City: this.shipmentPackage.HoldAtCity,
                State: this.shipmentPackage.HoldAtState,
                Zip: this.shipmentPackage.HoldAtZip,
                Country: this.shipmentPackage.HoldAtCountry,
            }
            : null;

    }

    private onShipmentPackageChanged(change: SimpleChange) {
        if (!change || !change.currentValue) {
            return;
        }

        this.storageService.set(this.storageName, change.currentValue);

        this.isReturnService = this.shipmentPackage.IsReturnService;

        this.labelImage = change.currentValue.LabelImage ? `data:image/jpg;base64,${ change.currentValue.LabelImage }` : '';
        this.labelPdf = change.currentValue.LabelImageFull ? `data:application/pdf;base64,${ change.currentValue.LabelImageFull }` : '';
        this.podPdf = change.currentValue.POD?.PODPdf ? `data:application/pdf;base64,${ change.currentValue.POD.PODPdf }` : '';

        let country = this.shipmentPackage?.ShipFromCountry === '' || this.shipmentPackage?.ShipFromCountry === undefined
            ? this.shipmentPackage?.ShipFrom?.Country
            : this.shipmentPackage?.ShipFromCountry;

        if (this.shipmentPackage.IsReturnService && this.shipmentPackage.IsInternational) { // MV3-4805
            this.commodityCurrencyCode = this.shipmentService.isGBUser(this.user) ? 'GBP' : 'EUR';
        } else {
            this.commodityCurrencyCode = this.countryService.getCurrency(country);
        }

        if (!this.isPendingShipment) {
            this.getHandheldSignature();
        }

        this.shouldShowCostEstimate = ['US', 'CA'].includes(this.shipmentPackage.ShipFrom.Country);

        this.carrierCode = this.carrierPipe.transform(this.shipmentPackage.CarrierCode).toString().toLowerCase();
        this.shipmentService.getCarrierKey(this.carrierCode)
            .subscribe(
                (carrierKeys) => {
                    if (!carrierKeys || !carrierKeys.length) {
                        return;
                    }

                    this.shouldShowBillToAccountNumber = !!carrierKeys[0].AccountNumber && carrierKeys[0].ShowRates;
                },
            );


        if (this.shipmentPackage.Estimator) {
            let totalAccessorialFees = 0;

            if (this.shipmentPackage?.Estimator?.[0]?.EstimatorDetail) {
                totalAccessorialFees = this.shipmentPackage.Estimator[0].EstimatorDetail.reduce((acc, detail) => {
                    return acc + (detail.AccessorialFee || 0);
                }, 0);
            }

            let estimatedValue = this.isEUUser && this.shipmentPackage.Estimator.length ? this.shipmentPackage.TotalCharges - totalAccessorialFees :
                this.shipmentPackage.Estimator[0]?.BaseRate;
            let totalValue = this.isEUUser && this.shipmentPackage.Estimator.length ? this.shipmentPackage.Estimator[0]?.AccessorialsCost + estimatedValue :
                this.shipmentPackage.Estimator[0]?.TotalCharges;

            // MV3-7081: TotalCost should show 0 if totalValue is undefined or NaN
            if (isNaN(totalValue)) {
                totalValue = 0;
            }

            this.estimatedCost = this.shipmentPackage.Estimator.length ?
                this.utilityService.formatMoney(estimatedValue, this.insuredValueCurrencyCode, this.locale) :
                this.utilityService.formatMoney(0, this.insuredValueCurrencyCode, this.locale);
            this.surcharge = this.shipmentPackage.Estimator.length ?
                this.utilityService.formatMoney(this.shipmentPackage.Estimator[0]?.AccessorialsCost, this.insuredValueCurrencyCode, this.locale) :
                this.utilityService.formatMoney(0, this.insuredValueCurrencyCode, this.locale);
            this.totalCharges = this.shipmentPackage.Estimator.length ?
                this.utilityService.formatMoney(totalValue, this.insuredValueCurrencyCode, this.locale) :
                this.utilityService.formatMoney(0, this.insuredValueCurrencyCode, this.locale);
            this.insuranceCost = this.shipmentPackage.InsuredValue ?
                this.utilityService.formatMoney(this.shipmentPackage.InsuredValue, this.insuredValueCurrencyCode, this.locale) :
                this.utilityService.formatMoney(0, this.insuredValueCurrencyCode, this.locale); // MV3-4821 insurance value is used instead of insurance cost
        }

        const isEditableShipmentStatus = (<Package>(change.currentValue)).Status === ShipmentStatus.BIRNew
            || (<Package>(change.currentValue)).Status === ShipmentStatus.LabelPrinted;
        this.isAllowEditShipment = SiteConfig.shouldAllowShipmentEdit && isEditableShipmentStatus;
    }

    private getHandheldSignature() {
        this.isHandheldSignatureLoading = true;
        this.utilityService.clearSubscriptions([this.getHandheldSignatureSubscription]);
        this.getHandheldSignatureSubscription = this.shipmentService.getHandheldSignature(this.shipmentPackage.ShipmentId)
                                                    .pipe(takeWhile(() => !this.handheldSignature))
                                                    .subscribe(
                                                        signature => this.handleGetHandheldSignatureSuccess(signature),
                                                        err => this.handleGetHandheldSignatureFailure(err),
                                                    );
    }

    private handleGetHandheldSignatureSuccess(signature: IHandheldSignature) {
        this.handheldSignature = signature;
        this.isHandheldSignatureLoading = false;
    }

    private handleGetHandheldSignatureFailure(err) {
        this.isHandheldSignatureLoading = false;
    }

    private warnNoLabel() {
        this.notificationService.notify('We cannot find label for this shipment', 'No Label', NotificationType.WARNING);
    }

    private getLabel() {
        this.utilityService.clearSubscriptions([this.getShipmentLabelSubscription]);
        this.getShipmentLabelSubscription = this.shipmentService.getShipmentLabel(this.shipmentPackage.ShipmentId)
                                                .subscribe(
                                                    res => this.handleGetShipmentLabelSuccess(res),
                                                    err => this.handleGetShipmentLabelFailure(err),
                                                );
    }

    private getTrackingDetails() {
        if (this.trackingScans) {
            this.dtTrigger.next(null);
            return;
        }

        this.utilityService.clearSubscriptions([this.getTrackingDetailsSubscription]);
        this.isTrackingDetailsLoading = true;
        this.getTrackingDetailsSubscription = this.reportHistoryService.getTrackingDetails(this.shipmentPackage.ShipmentId, this.shipmentType)
                                                  .subscribe(
                                                      trackingResult => this.handleGetTrackingDetailsSuccess(trackingResult),
                                                      err => this.handleGetTrackingDetailsFailure(err),
                                                  );
    }

    private handleGetTrackingDetailsSuccess(trackingResult: ITrackingResult) {
        this.utilityService.delay(() => {
            this.isTrackingDetailsLoading = false;
        });

        if (!trackingResult) {
            return;
        }

        this.trackingScans = this.formatDates(trackingResult.Scans);
        this.dtTrigger.next(null);
    }

    private handleGetTrackingDetailsFailure(err) {
        this.utilityService.delay(() => {
            this.isTrackingDetailsLoading = false;
        });
        this.notificationService.notify(
            this.errorHandlerService.getHttpErrorMessage(err),
            'Failed getting tracking details',
            NotificationType.ERROR);
        this.dtTrigger.next(null);
    }

    private formatDates(trackingScans: any) {
        this.lang = sessionStorage.getItem('lang');
        let dtFormat = ['de', 'gb', 'uk'].includes(this.lang) ? 'DD/MM/YYYY HH:mm:ss' : 'MM/DD/YYYY HH:mm:ss';

        this.sharedService.langSubject.subscribe(lang => {
            dtFormat = ['de', 'gb', 'uk'].includes(lang.toLowerCase()) ? 'DD/MM/YYYY HH:mm:ss' : 'MM/DD/YYYY HH:mm:ss';
        });
        if (trackingScans !== undefined && trackingScans !== null) {
            for (const scan of trackingScans) {
                scan.ActivityDate = moment(scan.ActivityDate).format(dtFormat);
            }
        }
        return trackingScans;
    }

    private generatePrintHtml(printContent: string, printCss: string): string {
        const md = new MobileDetect(window.navigator.userAgent);
        let htmlContent = `
        <html>
          <head>
            <title>Shipment Details</title>
            <style type="text/css" media="print">
              ${ printCss }
              table { page-break-after: auto !important; }
              tr    { page-break-inside:avoid !important; page-break-after:auto !important; }
              td    { page-break-inside:avoid !important; page-break-after:auto !important; }
              thead { display:table-row-group !important; }
              div   { page-break-inside:avoid !important; } 
              tfoot { display:table-footer-group !important; }
              body {  width: 21cm; height: 29.7cm; margin: 7.5mm 5mm 7.5mm 5mm; }
            </style>`;

        // [MV3-1866] An attempt to dynamically click the print button on Safari/iPhone.
        // Ref: https://www.quirksmode.org/blog/archives/2010/09/click_event_del.html
        if (md.phone() === 'iPhone') {
            htmlContent += `
            <script>
              (function() {
                  var printButton = document.getElementById('print');
                  printButton.onclick = function(){};
                  printButton.click();
              })();
            </script>`;
        }

        htmlContent += `</head><body onload="window.print();window.close()">`;

        if (md.phone() === 'iPhone') {
            htmlContent += `<button id="print" class="hide-for-print" onclick="window.print()" style="cursor: pointer;">Print</button>`;
        }

        htmlContent +=
            `<div>${ printContent }</div>
          </body>
        </html>`;

        return htmlContent;
    }
}
